import { Injectable, signal, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { TelnyxRTC, TelnyxConnectionOptions } from '@telnyx/webrtc';
import { environment } from '../../../environments/environment';
import { TELNYX_CONSTANTS } from './telnyx.constants';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'failed';

export interface ConnectionEvent {
  event: string;
  data?: any;
}

export interface TelnyxCredentials {
  login: string;
  password: string;
  domain?: string;
  realm?: string;
}

/**
 * TelnyxConnectionService - Manages WebRTC client lifecycle
 *
 * Responsibilities:
 * - Create and manage TelnyxRTC client instance
 * - Handle connection/disconnection
 * - Automatic reconnection with exponential backoff
 * - Network state monitoring (online/offline)
 * - Expose connection state via signals
 */
@Injectable({
  providedIn: 'root'
})
export class TelnyxConnectionService implements OnDestroy {
  private client: TelnyxRTC | null = null;
  private credentials: TelnyxCredentials | null = null;

  // Connection state signals
  private _connected = signal(false);
  private _connectionStatus = signal<ConnectionStatus>('disconnected');

  // Public readonly signals
  readonly connected = this._connected.asReadonly();
  readonly connectionStatus = this._connectionStatus.asReadonly();

  // Event stream
  private connectionEventsSubject = new Subject<ConnectionEvent>();
  readonly connectionEvents$ = this.connectionEventsSubject.asObservable();

  // Reconnection state
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isReconnecting = false;

  // Network listeners
  private networkOnlineListener: (() => void) | null = null;
  private networkOfflineListener: (() => void) | null = null;

  ngOnDestroy(): void {
    this.disconnect();
    this.removeNetworkListeners();
    this.connectionEventsSubject.complete();
  }

  /**
   * Get the current Telnyx client instance
   */
  getClient(): TelnyxRTC | null {
    return this.client;
  }

  /**
   * Connect to Telnyx with provided credentials
   */
  async connect(credentials: TelnyxCredentials): Promise<void> {
    this.credentials = credentials;
    this._connectionStatus.set('connecting');

    // Disconnect existing client if any
    if (this.client) {
      this.disconnectClient();
    }

    // Create new client
    this.client = this.createClient(credentials);
    this.setupNetworkListeners();

    // Connect
    this.client.connect();
  }

  /**
   * Disconnect from Telnyx
   */
  disconnect(): void {
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
    this.disconnectClient();
    this._connected.set(false);
    this._connectionStatus.set('disconnected');
  }

  /**
   * Force reconnection (e.g., after credential refresh)
   * Resets reconnect state so this always works even after max attempts exhausted.
   */
  async reconnect(credentials?: TelnyxCredentials): Promise<void> {
    if (credentials) {
      this.credentials = credentials;
    }

    if (!this.credentials) {
      throw new Error('No credentials available for reconnection');
    }

    this.clearReconnectState();
    await this.performReconnection();
  }

  /**
   * Register a callback for Telnyx notifications (call events)
   */
  onNotification(callback: (notification: any) => void): void {
    if (this.client) {
      this.client.on('telnyx.notification', callback);
    }
  }

  /**
   * Create Telnyx client with connection options
   */
  private createClient(credentials: TelnyxCredentials): TelnyxRTC {
    const options: TelnyxConnectionOptions = {
      login: credentials.login,
      password: credentials.password,
      dtmfType: 'rfc2833',
      logLevel: environment.production ? 'warn' : 'debug',
      enableVideo: false,
      enableAudio: true,
      ringtoneFile: '',
      ringbackFile: '',
      iceServers: TELNYX_CONSTANTS.ICE_SERVERS,
    };

    const client = new TelnyxRTC(options);
    this.setupClientEventListeners(client);
    return client;
  }

  /**
   * Setup event listeners for connection-related events
   */
  private setupClientEventListeners(client: TelnyxRTC): void {
    // Connection ready
    client.on('telnyx.ready', () => {
      const wasReconnecting = this.isReconnecting || this.reconnectAttempts > 0;

      this._connected.set(true);
      this._connectionStatus.set('connected');
      this.clearReconnectState();

      this.connectionEventsSubject.next({ event: 'ready' });

      if (wasReconnecting) {
        this.connectionEventsSubject.next({ event: 'reconnected' });
      }
    });

    // Socket closed
    client.on('telnyx.socket.close', (event) => {
      this._connected.set(false);
      this._connectionStatus.set('disconnected');
      this.connectionEventsSubject.next({ event: 'socket-close', data: event });

      // Attempt reconnection if not intentionally disconnected
      if (!this.isReconnecting && this.credentials) {
        this.attemptReconnection();
      }
    });

    // Socket error
    client.on('telnyx.socket.error', (error) => {
      this.connectionEventsSubject.next({ event: 'socket-error', data: error });
    });

    // General error
    client.on('telnyx.error', (error) => {
      this._connected.set(false);
      this._connectionStatus.set('failed');

      const errorType = this.classifyError(error);
      this.connectionEventsSubject.next({
        event: 'error',
        data: { error, errorType }
      });

      // For auth errors, emit specific event so parent can refresh credentials
      if (errorType === 'AUTH_ERROR') {
        this.connectionEventsSubject.next({
          event: 'auth-error',
          data: { error, message: 'Credentials expired or invalid' }
        });
      } else {
        this.attemptReconnection();
      }
    });
  }

  /**
   * Classify error type for proper handling
   */
  private classifyError(error: any): string {
    if (!error || typeof error !== 'object') {
      return 'CONNECTION_ERROR';
    }

    const message = error.message?.toLowerCase() || '';
    const code = error.code || error.status;

    // Telnyx returns -32000 for "Authentication Required" (JSON-RPC error)
    if (message.includes('authentication') || message.includes('unauthorized') ||
        code === 401 || code === -32000) {
      return 'AUTH_ERROR';
    }

    if (message.includes('network') || message.includes('timeout') ||
        message.includes('connection refused')) {
      return 'NETWORK_ERROR';
    }

    if (message.includes('registration') || message.includes('register')) {
      return 'SIP_REGISTRATION_ERROR';
    }

    return 'CONNECTION_ERROR';
  }

  /**
   * Attempt reconnection with exponential backoff
   */
  private attemptReconnection(): void {
    if (this.isReconnecting || !navigator.onLine) {
      return;
    }

    if (this.reconnectAttempts >= TELNYX_CONSTANTS.RECONNECT.MAX_ATTEMPTS) {
      this.connectionEventsSubject.next({
        event: 'error',
        data: {
          errorType: 'MAX_RECONNECT_ATTEMPTS',
          message: 'Unable to reconnect after multiple attempts'
        }
      });
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    const delay = Math.min(
      TELNYX_CONSTANTS.RECONNECT.BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts - 1),
      TELNYX_CONSTANTS.RECONNECT.MAX_DELAY_MS
    );

    this.connectionEventsSubject.next({
      event: 'reconnecting',
      data: { attempt: this.reconnectAttempts, delay }
    });

    this.reconnectTimer = setTimeout(async () => {
      try {
        await this.performReconnection();
        // connect() is fire-and-forget; allow next socket.close to trigger retry
        this.isReconnecting = false;
      } catch {
        this.isReconnecting = false;
        this.attemptReconnection();
      }
    }, delay);
  }

  /**
   * Perform the actual reconnection
   */
  private async performReconnection(): Promise<void> {
    if (!navigator.onLine || !this.credentials) {
      return;
    }

    this._connectionStatus.set('connecting');

    // Destroy old client
    this.disconnectClient();

    // Create fresh client
    this.client = this.createClient(this.credentials);
    this.client.connect();
  }

  /**
   * Disconnect and cleanup client
   */
  private disconnectClient(): void {
    if (this.client) {
      try {
        this.client.disconnect();
      } catch {
        // Ignore disconnect errors
      }
      this.client = null;
    }
  }

  /**
   * Setup network online/offline listeners
   */
  private setupNetworkListeners(): void {
    this.removeNetworkListeners();

    this.networkOnlineListener = () => {
      if (!this._connected()) {
        this.attemptReconnection();
      }
    };

    this.networkOfflineListener = () => {
      this._connected.set(false);
      this._connectionStatus.set('disconnected');
    };

    window.addEventListener('online', this.networkOnlineListener);
    window.addEventListener('offline', this.networkOfflineListener);
  }

  /**
   * Remove network listeners
   */
  private removeNetworkListeners(): void {
    if (this.networkOnlineListener) {
      window.removeEventListener('online', this.networkOnlineListener);
      this.networkOnlineListener = null;
    }
    if (this.networkOfflineListener) {
      window.removeEventListener('offline', this.networkOfflineListener);
      this.networkOfflineListener = null;
    }
  }

  /**
   * Clear reconnection timer
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Clear all reconnection state
   */
  private clearReconnectState(): void {
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
  }
}
